import { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { discoverAgentTools } from "../api/providers";
import listenerManager from "../services/ListenerManager";
import { GLOBAL_ACTIONS } from "../services/ListenerManager/constants";

export const getAgentToolDiscoveryLabel = (progress) => {
  switch (progress?.stage) {
    case "connecting":
      return "Connecting to MCP server";
    case "fetching_tools":
      return "Fetching available tools";
    case "classifying_tools":
      return progress.batch && progress.total_batches
        ? `Classifying tools (${progress.batch}/${progress.total_batches})`
        : "Classifying tools";
    case "finalizing":
      return "Preparing discovery results";
    default:
      return "Discovering tools";
  }
};

const useAgentToolDiscovery = () => {
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [progress, setProgress] = useState(null);
  const activeListenerId = useRef(null);
  const mounted = useRef(true);

  const removeActiveListener = () => {
    if (activeListenerId.current) {
      listenerManager.adapter.removeListener(activeListenerId.current);
      activeListenerId.current = null;
    }
  };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      removeActiveListener();
    };
  }, []);

  const discover = async (connection) => {
    removeActiveListener();
    const requestId = uuidv4();
    let settled = false;

    setIsDiscovering(true);
    setProgress(null);

    listenerManager.whenInitialized().then(() => {
      if (settled || !mounted.current || !listenerManager.isInitialized()) return;
      activeListenerId.current = listenerManager.adapter.createListener(
        `agent-tool-discovery/${requestId}`,
        GLOBAL_ACTIONS.AGENT_TOOL_DISCOVERY_PROGRESS,
        (event) => {
          if (mounted.current && event.request_id === requestId) setProgress(event);
        },
        () => {}
      );
    }).catch(() => {});

    try {
      return await discoverAgentTools({ ...connection, request_id: requestId });
    } finally {
      settled = true;
      removeActiveListener();
      if (mounted.current) {
        setIsDiscovering(false);
        setProgress(null);
      }
    }
  };

  return {
    discover,
    isDiscovering,
    progressLabel: getAgentToolDiscoveryLabel(progress),
  };
};

export default useAgentToolDiscovery;